import React, { createContext, useContext } from 'react';
import { Provider, FetchProviderProps } from 'use-http';
import createPersistedState from 'use-persisted-state';
import Login from './Login';

// A production build always talks to the real API. In local dev the default is the
// backend the dev stack exposes on :3000; REACT_APP_API_URL points a second frontend
// at a different local backend, so a branch can be run alongside the running stack.
export const url =
  process.env.NODE_ENV === 'production'
    ? 'https://api.sbly.com'
    : process.env.REACT_APP_API_URL || 'http://localhost:3000';

export const key = 'sbly-tools:accessToken';
const USER_TYPE_KEY = 'userType';

// Local dev only: sign in automatically so the stack stops asking for the
// 1Password entry.
//
// The session lives in localStorage, which is scoped per ORIGIN — and every
// worktree stack gets its own port (3001, 3011, 3021, ...). So each session was
// a fresh origin with an empty store and another trip to 1Password, for a token
// already sitting decrypted in sbly-dash-collector/src/config on the same
// machine. dev-stack.js reads it from there and passes it in, so there is no
// second copy of the secret to keep in sync or accidentally commit.
//
// Two things keep this out of production: the NODE_ENV guard, which CRA
// dead-code-eliminates from a production build, and the fact that the deploy
// workflow never sets the variable, so there is nothing to inline even if the
// guard were removed. It also never overwrites an existing session, so logging
// out as admin to test a `user` role still works.
function seedDevSession() {
  if (process.env.NODE_ENV === 'production') return;
  const devToken = process.env.REACT_APP_DEV_ACCESS_TOKEN;
  if (!devToken || localStorage.getItem(key)) return;
  // use-persisted-state stores JSON, so these have to be stringified to be
  // readable by the hooks below (and by BaseService, which strips the quotes).
  localStorage.setItem(key, JSON.stringify(devToken));
  localStorage.setItem(USER_TYPE_KEY, JSON.stringify('admin'));
}
seedDevSession();

const useAccessToken = createPersistedState(key);
const useUserType = createPersistedState(USER_TYPE_KEY);

interface AuthorizationProps {
  children: JSX.Element;
}

interface AuthContextType {
  userType: string | null;
  accessToken: string | null;
  handleLogin: (userType: string, accessToken: string) => void;
  handleLogout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  userType: null,
  accessToken: null,
  handleLogin: () => {},
  handleLogout: () => {},
});

export default function Authorization({ children }: AuthorizationProps) {
  const [accessToken, setAccessToken] = useAccessToken<string | null>(null);
  const [userType, setUserType] = useUserType<string | null>(null);

  const handleLogin = (userType: string, accessToken: string) => {
    setUserType(userType);
    setAccessToken(accessToken);
    window.location.reload();
  };

  const handleLogout = () => {
    setUserType(null);
    setAccessToken(null);
    window.location.reload();
  };

  const options: FetchProviderProps['options'] = {
    interceptors: {
      request({ options }: { options: RequestInit }) {
        if (accessToken) {
          options.headers = {
            ...options.headers,
            Authorization: `Bearer ${accessToken}`,
          };
        }
        return options;
      },
    },
  };

  if (accessToken) {
    return (
      <AuthContext.Provider
        value={{ userType, accessToken, handleLogin, handleLogout }}
      >
        <Provider url={url} options={options}>
          {children}
        </Provider>
      </AuthContext.Provider>
    );
  }

  return (
    <Login
      onSubmit={(userType, accessToken) => handleLogin(userType, accessToken)}
    />
  );
}

export const useAuth = () => useContext(AuthContext);
